import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Alert, Col, InputNumber, Modal, Row, Select, Spin, Table } from "antd";
import { useFormik } from "formik";
import { cloneDeep, get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import CarFuelRefund from "../../services/api/CarFuelRefund";
import CarEngineType from "../../services/api/CarEngineType";
import { paginationDefaults } from "../../utils/properties";
import { buildQueryString, showError, showSuccess, trimData } from "../../utils/utilities";
import Filter from "../../components/functional_components/Filter/Filter";
import { setFilters } from "../../redux/ducks/Filters";

const CarFuelRefunds = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { confirm } = Modal;
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
    carEngineTypeList: [],
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  useEffect(() => {
    getCarEngineType();
  }, [state.tableContent]);

  const formikCarFuelRefund = useFormik({
    initialValues: {
      ccFrom: get(state.modal.data, "ccFrom", null),
      ccTo: get(state.modal.data, "ccTo", null),
      carEngineType: get(state.modal.data, "carEngineType.id", null),
      valueKm: get(state.modal.data, "valueKm", null),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (values.ccFrom !== 0 && !values.ccFrom) {
        errors.ccFrom = "Required!";
      }
      if (values.ccTo !== 0 && !values.ccTo) {
        errors.ccTo = "Required!";
      }
      if (!values.carEngineType) {
        errors.carEngineType = "Required!";
      }
      if (!values.valueKm) {
        errors.valueKm = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        ...values,
        carEngineType: state.carEngineTypeList.find((c) => c.id === values.carEngineType),
        id: state.modal.data?.id || undefined,
      };
      const saveResponse = await CarFuelRefund.setCarFuelRefund(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, {});
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    let tableContent = [];
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await CarFuelRefund.getCarFuelRefunds(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        tableContent.push({
          ...cc,
          key: i, // This is necessary to remove warning in console for unique key row
          description: cc.carEngineType?.description,
          ccFrom: `${cc.ccFrom} ${cc.carEngineType?.measurementDesc}`,
          ccTo: `${cc.ccTo} ${cc.carEngineType?.measurementDesc}`,
        });
      });
      setState((prevState) => ({
        ...prevState,
        tableContent,
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikCarFuelRefund.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await CarFuelRefund.getCarFuelRefund(id);
    if (!error && data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data } }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await CarFuelRefund.deleteCarFuelRefund(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const getCarEngineType = async () => {
    const { error, data } = await CarEngineType.getCarEngineTypes(`allPages=true`);
    if (!error && data) {
      setState((prevState) => ({
        ...prevState,
        carEngineTypeList: data,
      }));
    }
  };

  const columns = [
    {
      title: t("carFuelRefund.table.ccFrom"),
      dataIndex: "ccFrom",
      key: "ccFrom",
    },
    {
      title: t("carFuelRefund.table.ccTo"),
      dataIndex: "ccTo",
      key: "ccTo",
    },
    {
      title: t("carFuelRefund.table.description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("carFuelRefund.table.valueKm"),
      dataIndex: "valueKm",
      key: "valueKm",
    },
    {
      title: t("carFuelRefund.table.carEngineType"),
      dataIndex: "carEngineType",
      key: "carEngineType",
      render: (data) => `${data.name}`,
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(data.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const formikFilter = useFormik({
    initialValues: {
      ccFrom: "",
      ccTo: "",
      carEngineType: "",
      valueKm: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      let dataFilter = cloneDeep(values);
      dataFilter["page"] = 0;
      dataFilter["carEngineType.id"] = values.carEngineType;
      delete dataFilter.carEngineType;
      const queryString = buildQueryString(filters.obj, dataFilter);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "ccFrom",
      name: "ccFrom",
      value: formikFilter.values.ccFrom,
      label: t("carFuelRefund.table.ccFrom"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "ccTo",
      name: "ccTo",
      value: formikFilter.values.ccTo,
      label: t("carFuelRefund.table.ccTo"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "select",
      isRequired: false,
      id: "carEngineType",
      name: "carEngineType",
      value: formikFilter.values.carEngineType,
      label: t("carFuelRefund.table.carEngineType"),
      onChange: (e) => formikFilter.setFieldValue("carEngineType", e),
      onBlur: formikFilter.handleBlur("carEngineType"),
      // onSearch: (e) => onSearchCarEngineType(e),
      dataSelect: state.carEngineTypeList.map((u) => ({ value: u.id, label: u.name })),
    },
    {
      type: "input",
      isRequired: false,
      id: "valueKm",
      name: "valueKm",
      value: formikFilter.values.valueKm,
      label: t("carFuelRefund.table.valueKm"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("carFuelRefund.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikCarFuelRefund.submitForm}
            isDisabled={!(formikCarFuelRefund.dirty && formikCarFuelRefund.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row>
          <Col span={24}>
            <Alert message={t("carFuelRefund.infoBox")} type="info" />
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="ccFrom">
                {t("carFuelRefund.table.ccFrom")}*
              </label>
            </Row>
            <Row>
              <InputNumber
                className="ant-input"
                controls={false}
                required
                id="ccFrom"
                name="ccFrom"
                onChange={(e) => formikCarFuelRefund.setFieldValue("ccFrom", e)}
                onBlur={formikCarFuelRefund.handleBlur}
                value={formikCarFuelRefund.values.ccFrom}
                precision={0}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="ccTo">
                {t("carFuelRefund.table.ccTo")}*
              </label>
            </Row>
            <Row>
              <InputNumber
                className="ant-input"
                controls={false}
                required
                id="ccTo"
                name="ccTo"
                onChange={(e) => formikCarFuelRefund.setFieldValue("ccTo", e)}
                onBlur={formikCarFuelRefund.handleBlur}
                value={formikCarFuelRefund.values.ccTo}
                precision={0}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="carEngineType">
                {t("carFuelRefund.table.carEngineType")}*
              </label>
            </Row>
            <Row>
              <Select
                className="w-100"
                id="carEngineType"
                name="carEngineType"
                placeholder={t("carFuelRefund.table.carEngineType")}
                onChange={(value) => {
                  formikCarFuelRefund.setFieldValue("carEngineType", value);
                }}
                onBlur={() => {
                  formikCarFuelRefund.handleBlur("carEngineType");
                }}
                value={formikCarFuelRefund.values.carEngineType}
                options={state.carEngineTypeList.map((u) => ({ value: u.id, label: u.name }))}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="valueKm">
                {t("carFuelRefund.table.valueKm")}*
              </label>
            </Row>
            <Row>
              <InputNumber
                className="ant-input"
                controls={false}
                required
                id="valueKm"
                name="valueKm"
                onChange={(e) => formikCarFuelRefund.setFieldValue("valueKm", e)}
                onBlur={formikCarFuelRefund.handleBlur}
                value={formikCarFuelRefund.values.valueKm}
                precision={2}
                decimalSeparator=","
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default CarFuelRefunds;
